import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { PlusIcon, CopyIcon, CheckIcon } from 'lucide-react'
import type { LoraPreviewDTO } from '@/lib/types'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

interface LoraSelectorPopupProps {
  availableLoras: LoraPreviewDTO[]
  selectedLoras: Record<number, number>
  onAddLora: (lora: LoraPreviewDTO) => void
  onRemoveLora: (loraId: number) => void
  children: React.ReactNode
}

export function LoraSelectorPopup({
  availableLoras,
  selectedLoras,
  onAddLora,
  onRemoveLora,
  children,
}: LoraSelectorPopupProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [copiedTriggerWords, setCopiedTriggerWords] = useState<Set<string>>(new Set())

  const handleCopyTriggerWord = async (triggerWord: string, loraName: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent dialog close and LoRA selection
    try {
      await navigator.clipboard.writeText(`${triggerWord} (from ${loraName})`)
      toast.success(`Copied "${triggerWord}" to clipboard`)

      // Add to copied set and remove after 2 seconds
      setCopiedTriggerWords(prev => new Set(prev).add(triggerWord))
      setTimeout(() => {
        setCopiedTriggerWords(prev => {
          const next = new Set(prev)
          next.delete(triggerWord)
          return next
        })
      }, 2000)
    } catch (error) {
      // Fallback for browsers that don't support the Clipboard API
      const textArea = document.createElement('textarea')
      textArea.value = triggerWord
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand('copy')
        toast.success(`Copied "${triggerWord}" to clipboard`)

        // Add to copied set and remove after 2 seconds
        setCopiedTriggerWords(prev => new Set(prev).add(triggerWord))
        setTimeout(() => {
          setCopiedTriggerWords(prev => {
            const next = new Set(prev)
            next.delete(triggerWord)
            return next
          })
        }, 2000)
      } catch (fallbackError) {
        toast.error('Failed to copy to clipboard')
      } finally {
        document.body.removeChild(textArea)
      }
    }
  }

  const filteredLoras = useMemo(() => {
    if (!search) return availableLoras // Show all LoRAs
    const lower = search.toLowerCase()
    return availableLoras.filter(
      (lora) =>
        lora.display_name.toLowerCase().includes(lower) ||
        lora.slug.toLowerCase().includes(lower) ||
        lora.trigger_words.some((word) => word.toLowerCase().includes(lower))
    ) // Show all matching LoRAs
  }, [availableLoras, search])

  const handleLoraClick = (lora: LoraPreviewDTO) => {
    if (selectedLoras[lora.id] !== undefined) {
      onRemoveLora(lora.id)
    } else {
      onAddLora(lora)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Select LoRA Models</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Input
              placeholder="Search LoRAs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
          </div>

          <ScrollArea className="h-[500px] w-full rounded-md border">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
              {filteredLoras.map((lora) => {
                const isSelected = selectedLoras[lora.id] !== undefined

                return (
                  <div
                    key={lora.id}
                    className={cn(
                      'relative group cursor-pointer border-2 rounded-lg overflow-hidden transition-all hover:scale-105',
                      isSelected
                        ? 'border-accent bg-accent/10'
                        : 'border-border hover:border-accent/50'
                    )}
                    onClick={() => handleLoraClick(lora)}
                  >
                    <div className="aspect-square relative">
                      {lora.thumbnail_url ? (
                        <img
                          src={lora.thumbnail_url}
                          alt={lora.display_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <div className="text-center text-muted-foreground p-2">
                            <p className="text-xs font-medium truncate">
                              {lora.civitai_url ? (
                                <a
                                  href={lora.civitai_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-accent hover:text-accent/80 underline decoration-accent/30 hover:decoration-accent/60 transition-colors"
                                  title={`View ${lora.display_name} on Civitai`}
                                  onClick={(e) => e.stopPropagation()} // Prevent dialog close when clicking link
                                >
                                  {lora.display_name}
                                </a>
                              ) : (
                                lora.display_name
                              )}
                            </p>
                            <p className="text-[10px]">No preview</p>
                          </div>
                        </div>
                      )}

                      {isSelected && (
                        <div className="absolute inset-0 bg-accent/20 flex items-center justify-center">
                          <div className="bg-accent text-accent-foreground rounded-full p-2">
                            <PlusIcon className="h-4 w-4" />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-2 bg-background">
                      <h3 className="text-sm font-medium truncate">
                        {lora.civitai_url ? (
                          <a
                            href={lora.civitai_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent hover:text-accent/80 underline decoration-accent/30 hover:decoration-accent/60 transition-colors"
                            title={`View ${lora.display_name} on Civitai`}
                            onClick={(e) => e.stopPropagation()} // Prevent dialog close when clicking link
                          >
                            {lora.display_name}
                          </a>
                        ) : (
                          lora.display_name
                        )}
                      </h3>
                      {lora.trigger_words.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {lora.trigger_words.map((triggerWord, index) => (
                            <button
                              key={index}
                              onClick={(e) => handleCopyTriggerWord(triggerWord, lora.display_name, e)}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-muted/50 hover:bg-muted/80 rounded text-xs transition-colors cursor-pointer group"
                              title={`Click to copy "${triggerWord}"`}
                            >
                              <span className="group-hover:text-accent truncate max-w-[80px]">{triggerWord}</span>
                              {copiedTriggerWords.has(triggerWord) ? (
                                <CheckIcon className="h-2.5 w-2.5 text-green-500 animate-pulse flex-shrink-0" />
                              ) : (
                                <CopyIcon className="h-2.5 w-2.5 text-accent flex-shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {filteredLoras.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No LoRAs found</p>
              </div>
            )}
          </ScrollArea>

          <div className="flex justify-between items-center pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              {Object.keys(selectedLoras).length} LoRA{Object.keys(selectedLoras).length !== 1 ? 's' : ''} selected
            </p>
            <Button
              onClick={() => setOpen(false)}
              variant="outline"
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
